import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import UPButton from 'components/button';
import { UPDataGrid } from 'components/data-grid';
import UPDatepicker from 'components/datepicker';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import CATEGORIES from 'constants/Categories';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  addMinimumWageAction,
  getMinimumWagesAction,
  updateMinimumWageAction,
} from 'store/actions/minimum-wages-actions';
import {
  getCategoriesDescriptionsAction,
  updateCategoryDescriptionAction,
} from 'store/actions/paritair-comite-actions';
import { minimumWagesSelector } from 'store/selectors/minimum-wages-selectors';
import {
  EMPLOYEES__STUDENT_FULL,
  EMPLOYEES__FLEXI_FULL,
  EMPLOYEES__EXTRA_FULL,
  PARITAIR_COMITE__ADD_NEW_ROW,
  PARITAIR_COMITE__CATEGORIES_DESCRIPTION,
  PARITAIR_COMITE__END_DATE,
  PARITAIR_COMITE__EXPERIENCE_OR_AGE,
  PARITAIR_COMITE__FILTER_BY_DATE,
  PARITAIR_COMITE__START_DATE,
  PARITAIR_COMITE__WORKER_CLASS,
} from 'translations/constants';
import { IMinimumWage } from 'types/minimum-wage';
import ICategoriesDescription from 'types/minimum-wage/ICategoriesDescription';
import { ICategoryDescription } from 'types/paritair-comite';
import {
  ButtonContainer,
  CategoriesDescriptionsContainer,
  MinimumWageGridContainer,
  TopSectionContainer,
  TopSectionContainerWithMargins,
} from '../styles';
import { createEmptyCategoriesDescription } from './helpers';
import { MinimumWagePanelProps } from './types';

const useStyles = makeStyles({
  wrapText: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: '1.5',
  },
});

const MinimumWagePanel = (props: MinimumWagePanelProps): JSX.Element => {
  const classes = useStyles();
  const { type } = props;

  const [t] = useTranslation();

  const WORKERCLASS_OPTIONS: UPDropdownItem[] = [
    { value: 'OTH', label: 'General (OTH)' },
    { value: 'STUD', label: t(EMPLOYEES__STUDENT_FULL) },
    { value: 'FLEX', label: t(EMPLOYEES__FLEXI_FULL) },
    { value: 'EXT', label: t(EMPLOYEES__EXTRA_FULL) },
  ];

  const dispatch = useAppDispatch();
  const { paritairComiteId } = useParams();

  const minimumWagesList = useAppSelector(minimumWagesSelector);

  const [, setRowOnHover] = useState<number>();
  const [selectedWorkerclass, setSelectedWorkerclass] = useState<string>(WORKERCLASS_OPTIONS[0].value);
  const [dateFilter, setDateFilter] = useState<Date>(new Date());
  const [minimumWages, setMinimumWages] = useState<IMinimumWage[]>([]);
  const [categoriesDescriptions, setCategoriesDescriptions] = useState<ICategoriesDescription[]>([
    createEmptyCategoriesDescription(),
  ]);

  const categories = useMemo((): UPDropdownItem[] => CATEGORIES, []);

  const requestSearch = (searchValue: Date) => {
    setDateFilter(searchValue);
  };

  const onAddNewRow = useCallback(() => {
    setMinimumWages(prevState => [
      ...prevState,
      {
        id: Math.random(),
        paritairComiteId: toNumber(paritairComiteId),
        type,
        workerclass: selectedWorkerclass,
        yearsOfExperience: null,
        category1: null,
        category2: null,
        category3: null,
        category4: null,
        category5: null,
        category6: null,
        category7: null,
        category8: null,
        category9: null,
        category10: null,
        category11: null,
        category12: null,
        category13: null,
        category14: null,
        category15: null,
        startDate: null,
        endDate: null,
      },
    ]);
  }, [paritairComiteId, selectedWorkerclass, type]);

  const addOrUpdateEntity = useCallback(
    (entity: IMinimumWage, field: string, value: any): IMinimumWage => {
      const updatedEntity: IMinimumWage = {
        ...entity,
        [field]: value,
      };
      if (updatedEntity.id >= 0 && updatedEntity.id < 1) {
        if (updatedEntity.startDate != null && updatedEntity.endDate != null) {
          dispatch(addMinimumWageAction(updatedEntity));
        }
      } else {
        dispatch(updateMinimumWageAction(updatedEntity));
      }
      return updatedEntity;
    },
    [dispatch],
  );

  const padTo2Digits = useCallback(num => {
    return num.toString().padStart(2, '0');
  }, []);

  const formatDate = useCallback(
    date => {
      const auxDate = new Date(date);
      return [auxDate.getFullYear(), padTo2Digits(auxDate.getMonth() + 1), padTo2Digits(auxDate.getDate())].join('-');
    },
    [padTo2Digits],
  );

  const getDataGridColumns = useCallback((): GridColDef[] => {
    return categories.map(
      (_, index: number): GridColDef => ({
        field: `category${index + 1}`,
        headerName: `Cat ${index + 1}`,
        width: 90,
        editable: true,
        sortable: false,
        valueSetter: params => {
          if (Number.isNaN(toNumber(params.value)) || !params.value) {
            return params.row;
          }
          return addOrUpdateEntity(params.row as IMinimumWage, `category${index + 1}`, toNumber(params.value));
        },
        preProcessEditCellProps: (params: any) => {
          const hasError = Number.isNaN(toNumber(params.props.value));
          return { ...params.props, error: hasError };
        },
      }),
    );
  }, [categories, addOrUpdateEntity]);

  const addOrUpdateDescription = useCallback(
    (
      entity: ICategoriesDescription,
      field: string,
      value: string | null,
      categoryNumber: number,
    ): ICategoriesDescription => {
      const updatedEntity: ICategoriesDescription = {
        ...entity,
        [field]: value,
      };
      const updatedCategoryDescription: ICategoryDescription = {
        id: 0,
        paritairComiteId: toNumber(paritairComiteId),
        category: categoryNumber,
        collarType: type,
        description: value,
      };
      dispatch(updateCategoryDescriptionAction(updatedCategoryDescription));
      return updatedEntity;
    },
    [dispatch, paritairComiteId, type],
  );

  const getCategoryDescriptionColumns = useCallback((): GridColDef[] => {
    return categories.map(
      (_, index: number): GridColDef => ({
        field: `category${index + 1}`,
        headerName: `Cat ${index + 1}`,
        width: 120,
        editable: true,
        sortable: false,
        valueSetter: params => {
          return addOrUpdateDescription(
            params.row as ICategoriesDescription,
            `category${index + 1}`,
            params.value ? String(params.value) : null,
            index + 1,
          );
        },
        cellClassName: 'wrap-text',
      }),
    );
  }, [categories, addOrUpdateDescription]);

  useEffect(() => {
    if (minimumWagesList) {
      setMinimumWages(minimumWagesList);
    }
  }, [minimumWagesList]);

  useEffect(() => {
    dispatch(
      getMinimumWagesAction({
        paritairComiteId: toNumber(paritairComiteId),
        type,
        workerclass: selectedWorkerclass,
        dateFilter,
      }),
    );
  }, [paritairComiteId, type, selectedWorkerclass, dateFilter, dispatch]);

  const transformDescriptionsToRows = (
    descriptions: { category: string; description: string }[],
  ): ICategoriesDescription[] => {
    const rows: ICategoriesDescription[] = [];
    rows[0] = {
      id: 0,
      category1: null,
      category2: null,
      category3: null,
      category4: null,
      category5: null,
      category6: null,
      category7: null,
      category8: null,
      category9: null,
      category10: null,
      category11: null,
      category12: null,
      category13: null,
      category14: null,
      category15: null,
    };
    descriptions.forEach(({ category, description }) => {
      const categoryIndex = parseInt(category.replace('Category ', ''), 10);
      const columnName = `category${categoryIndex}`;
      rows[0][columnName] = description;
    });

    return rows;
  };
  useEffect(() => {
    const fetchDescriptions = async () => {
      try {
        const descriptions = await dispatch(
          getCategoriesDescriptionsAction({ paritairComiteId, collarType: type }),
        ).unwrap();
        if (descriptions.length !== 0) {
          const transformedRows = transformDescriptionsToRows(descriptions);
          setCategoriesDescriptions(transformedRows);
        }
      } catch (error) {
        console.error('An unexpected error occurred:', error);
      }
    };

    fetchDescriptions();
  }, [dispatch, paritairComiteId, type]);

  const workerclassColumns: GridColDef[] = [
    { field: 'id', headerName: '', hide: true },
    {
      field: 'yearsOfExperience',
      headerName: t(PARITAIR_COMITE__EXPERIENCE_OR_AGE),
      width: 160,
      editable: true,
      sortable: false,
      sortingOrder: ['asc'],
      valueSetter: params => {
        if (Number.isNaN(toNumber(params.value))) {
          return params.row;
        }
        return addOrUpdateEntity(params.row as IMinimumWage, `yearsOfExperience`, toNumber(params.value));
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = Number.isNaN(toNumber(params.props.value));
        return { ...params.props, error: hasError };
      },
    },
    ...getDataGridColumns(),
    {
      field: 'startDate',
      headerName: t(PARITAIR_COMITE__START_DATE),
      width: 150,
      editable: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Date>) => {
        if (params.value !== null) {
          const dateValue = new Date(params.value);
          return (
            <Box>
              {dateValue.getDate()}-{dateValue.getMonth() + 1}-{dateValue.getFullYear()}
            </Box>
          );
        }
        return <Box />;
      },
      type: 'date',
      valueSetter: params => {
        const hasError = params.row.endDate !== null && params.value > new Date(params.row.endDate);
        if (!hasError) {
          return addOrUpdateEntity(params.row as IMinimumWage, `startDate`, formatDate(params.value as Date));
        }
        return params.row;
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = params.row.endDate !== null && params.props.value > new Date(params.row.endDate);
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'endDate',
      headerName: t(PARITAIR_COMITE__END_DATE),
      width: 150,
      editable: true,
      sortable: false,
      type: 'date',
      renderCell: (params: GridRenderCellParams<Date>) => {
        if (params.value !== null) {
          const dateValue = new Date(params.value);
          return (
            <Box>
              {dateValue.getDate()}-{dateValue.getMonth() + 1}-{dateValue.getFullYear()}
            </Box>
          );
        }
        return <Box />;
      },
      valueSetter: params => {
        const hasError = params.row.startDate !== null && params.value < new Date(params.row.startDate);
        if (!hasError) {
          return addOrUpdateEntity(params.row as IMinimumWage, `endDate`, formatDate(params.value as Date));
        }
        return params.row;
      },
      preProcessEditCellProps: (params: any) => {
        const hasError = params.row.startDate !== null && params.props.value < new Date(params.row.startDate);
        return { ...params.props, error: hasError };
      },
    },
  ];

  return (
    <Box>
      <TopSectionContainerWithMargins>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '400px' }}>
          <UPDropdown
            items={WORKERCLASS_OPTIONS}
            onChange={event => setSelectedWorkerclass(event.target.value)}
            value={selectedWorkerclass}
            label={t(PARITAIR_COMITE__WORKER_CLASS)}
          />
        </Box>
        {selectedWorkerclass !== '' && (
          <TopSectionContainer sx={{ marginTop: '16px' }}>
            <ButtonContainer>
              <UPButton text={`+ ${t(PARITAIR_COMITE__ADD_NEW_ROW)}`} onClick={onAddNewRow} />
            </ButtonContainer>
            <UPDatepicker
              label={t(PARITAIR_COMITE__FILTER_BY_DATE)}
              value={dateFilter}
              onChange={event => requestSearch(event)}
            />
          </TopSectionContainer>
        )}
      </TopSectionContainerWithMargins>
      <MinimumWageGridContainer>
        <UPDataGrid
          rows={minimumWages}
          columns={workerclassColumns}
          setRowOnHover={setRowOnHover}
          withCheckboxes={false}
        />
      </MinimumWageGridContainer>
      <CategoriesDescriptionsContainer>
        <Typography>{t(PARITAIR_COMITE__CATEGORIES_DESCRIPTION)}</Typography>
        <MinimumWageGridContainer>
          <UPDataGrid
            rows={categoriesDescriptions}
            columns={getCategoryDescriptionColumns()}
            setRowOnHover={setRowOnHover}
            withCheckboxes={false}
            getRowHeight={() => '80px'}
            sx={{
              height: '300px',
              '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: '1.5',
              },
            }}
          />
        </MinimumWageGridContainer>
      </CategoriesDescriptionsContainer>
    </Box>
  );
};

export default MinimumWagePanel;
